import { useEffect } from 'react';
import { Input, Select } from 'antd';
import { COUNTRIES, DEFAULT_COUNTRY, buildPhone, splitPhone } from '../../utils/phone';

/**
 * Telefon alanı — ülke kodu seçici + numara.
 *
 * Form.Item'a yazılan değer KANONİK biçimdir (ülke kodu + ulusal numara,
 * artı/boşluk yok): '905372799324'. Backend de aynı biçimi saklıyor, veli
 * girişi bu değere bakıyor ve `users.phone` bu değer üzerinde benzersiz.
 *
 * NEDEN ÜLKE KODU EKLENDİ (13.09.2026): kolon `varchar(10)` ve doğrulama
 * `digits:10` olduğu için yurt dışı numaralı veliler numaralarını
 * GİREMİYORDU. Veli sisteme telefonuyla giriyor (AuthController::parentLogin)
 * ve telefonuyla üyelik aktifleştiriyor — yani o veliler hesaplarını hiç
 * açamıyor, uygulamaya da giremiyordu. WhatsApp bağlantılarının bozuk olması
 * aynı kökün daha görünür ama küçük sonucuydu.
 *
 * VARSAYILAN +90: mevcut 500+ veli numarasını ülke kodsuz yazıyor ve yazmaya
 * devam edecek.
 *
 * maxLength BİLEREK GENİŞ: HTML maxLength yapıştırmayı onChange'e ulaşmadan
 * kırpıyor — "+90 537 279 93 24" yapıştırıldığında ilk 10 karakter alınıp
 * gerisi atılıyordu.
 */
export default function PhoneInput({ value, onChange, disabled, ...rest }) {
  // Tek doğruluk kaynağı dışarıdaki değer; ülke ve numara her render'da
  // ondan türetiliyor. Ayrı bir state tutulsaydı form dışarıdan doldurulunca
  // (düzenleme modalı) ikisi birbirinden sapardı.
  const { country, national } = splitPhone(value);

  // DEĞER "+" İLE GÖNDERİLİYOR — bu bilinçli ve kritik.
  //
  // Backend ülke kodu görmediğinde +90 varsayıyor (mevcut velilerin girişi
  // bozulmasın diye). Forma artısız '447911123456' yazılsaydı backend bunu
  // ulusal numara sanıp başına 90 ekler, '90447911123456' kaydederdi.
  // Baştaki "+" backend'e "ülke kodunu ben söylüyorum" demek; böylece her
  // ekran için ayrıca `phone_country` alanı göndermek gerekmiyor.
  const emit = (nextCountry, nextNational) => {
    const kanonik = buildPhone(nextCountry, nextNational);
    onChange?.(kanonik ? `+${kanonik}` : '');
  };

  // API'DEN GELEN DEĞERİ "+" BİÇİMİNE ÇEKER — bu olmadan sessiz veri
  // bozulması olurdu.
  //
  // Düzenleme formları alanı doğrudan API değeriyle dolduruyor ('905551112233',
  // artısız). Kullanıcı telefona HİÇ DOKUNMADAN başka bir alanı değiştirip
  // kaydederse o artısız değer sunucuya gider; sunucu ülke kodu görmediği için
  // +90 varsayar ve yurt dışı bir numara '90447911123456' olarak kaydedilir.
  // Veli o andan itibaren giriş yapamaz.
  //
  // Düzeltmeyi beş ayrı formda tekrarlamak yerine burada yapıyoruz; alan
  // zaten hepsinde ortak.
  useEffect(() => {
    if (value && !String(value).startsWith('+')) {
      const mevcut = splitPhone(value);
      const kanonik = buildPhone(mevcut.country, mevcut.national);
      if (kanonik) onChange?.(`+${kanonik}`);
    }
    // onChange kasten bağımlılıkta değil: Form.Item her render'da yeni bir
    // fonksiyon veriyor ve eklenirse döngü olur.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <Input
      value={national}
      onChange={(e) => emit(country, e.target.value)}
      placeholder="5321234567"
      maxLength={25}
      inputMode="tel"
      disabled={disabled}
      addonBefore={
        <Select
          value={country || DEFAULT_COUNTRY}
          onChange={(c) => emit(c, national)}
          disabled={disabled}
          style={{ width: 110 }}
          showSearch
          // Kullanıcı hem "Almanya" hem "49" yazarak arayabilmeli.
          filterOption={(input, option) =>
            (option?.arama ?? '').toLowerCase().includes(input.toLowerCase())
          }
          options={COUNTRIES.map((c) => ({
            value: c.code,
            arama: `${c.name} ${c.code}`,
            label: `${c.flag} +${c.code}`,
          }))}
        />
      }
      {...rest}
    />
  );
}
